import { useState, useCallback } from 'react';
import {
  PCCase,
  CaseEntryStep,
  CaseEntryState,
  FieldConfidence,
  ValidationError,
  UserSubmittedCase,
} from '../types';
import { fetchPageContent } from '../services/urlFetchService';
import { extractCaseData } from '../services/aiExtractionService';
import { validateCase } from '../services/caseValidationService';
import { saveUserCase } from '../services/userCasesService';

interface UseCaseEntryResult {
  // State
  step: CaseEntryStep;
  extractedData: Partial<PCCase> | null;
  confidenceScores: FieldConfidence[];
  editedData: Partial<PCCase>;
  validationErrors: ValidationError[];
  isLoading: boolean;
  error: string | null;
  savedCase: UserSubmittedCase | null;

  // Actions
  extractFromUrl: (url: string) => Promise<void>;
  updateField: (field: string, value: unknown) => void;
  validate: () => boolean;
  save: () => Promise<UserSubmittedCase | null>;
  reset: () => void;
}

const initialState: CaseEntryState = {
  step: 'form',
  extractedData: null,
  confidenceScores: [],
  editedData: {},
  validationErrors: [],
  isLoading: false,
  error: null,
  savedCase: null,
};

export const useCaseEntry = (): UseCaseEntryResult => {
  const [state, setState] = useState<CaseEntryState>(initialState);

  const extractFromUrl = useCallback(async (url: string) => {
    if (!url.trim()) {
      setState((prev) => ({
        ...prev,
        error: 'Please enter a URL',
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      step: 'extracting',
      isLoading: true,
      error: null,
    }));

    try {
      const fetchResult = await fetchPageContent(url);

      if (!fetchResult.success) {
        setState((prev) => ({
          ...prev,
          step: 'form',
          isLoading: false,
          error: fetchResult.error || 'Failed to fetch page content',
        }));
        return;
      }

      const result = await extractCaseData(fetchResult.content!);

      if (!result.success) {
        setState((prev) => ({
          ...prev,
          step: 'form',
          isLoading: false,
          error: result.error || 'Failed to extract case data',
        }));
        return;
      }

      // Merge extracted data into editedData, keeping any existing edits
      // Prefer: AI-extracted imageUrl > HTML og:image > existing value
      const imageUrl = result.data.imageUrl || fetchResult.imageUrl || undefined;

      setState((prev) => ({
        ...prev,
        step: 'form',
        extractedData: result.data,
        confidenceScores: result.confidenceScores,
        editedData: {
          ...prev.editedData,
          ...result.data,
          productPageUrl: url,
          ...(imageUrl && !prev.editedData.imageUrl ? { imageUrl } : {}),
        },
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        step: 'form',
        isLoading: false,
        error: error instanceof Error ? error.message : 'Extraction failed',
      }));
    }
  }, []);

  const updateField = useCallback((field: string, value: unknown) => {
    setState((prev) => {
      const newData = { ...prev.editedData };

      // Handle nested fields (e.g., "dimensions.height")
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        const parentObj = (newData as Record<string, unknown>)[parent];
        (newData as Record<string, unknown>)[parent] = {
          ...(parentObj as Record<string, unknown> || {}),
          [child]: value,
        };
      } else {
        (newData as Record<string, unknown>)[field] = value;
      }

      // Clear validation errors for this field
      const newErrors = prev.validationErrors.filter((e) => e.field !== field);

      return {
        ...prev,
        editedData: newData,
        validationErrors: newErrors,
      };
    });
  }, []);

  const validate = useCallback((): boolean => {
    const result = validateCase(state.editedData);

    setState((prev) => ({
      ...prev,
      validationErrors: result.errors,
    }));

    return result.isValid;
  }, [state.editedData]);

  const save = useCallback(async (): Promise<UserSubmittedCase | null> => {
    if (!validate()) {
      return null;
    }

    try {
      const savedCase = await saveUserCase(state.editedData);

      setState((prev) => ({
        ...prev,
        step: 'success',
        savedCase,
        error: null,
      }));

      return savedCase;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to save case',
      }));
      return null;
    }
  }, [state.editedData, validate]);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    // State
    step: state.step,
    extractedData: state.extractedData,
    confidenceScores: state.confidenceScores,
    editedData: state.editedData,
    validationErrors: state.validationErrors,
    isLoading: state.isLoading,
    error: state.error,
    savedCase: state.savedCase,

    // Actions
    extractFromUrl,
    updateField,
    validate,
    save,
    reset,
  };
};
